import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Eye, CheckCircle, XCircle, HelpCircle, AlertCircle,
  MessageSquare, User, FileText, Clock, GraduationCap,
  Briefcase, MapPin, Award, Calendar, Shield, History
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const CommitteePortal = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appDetails, setAppDetails] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [comment, setComment] = useState("");
  const [filters, setFilters] = useState({ status: "" });
  const [pagination, setPagination] = useState({ page: 1, total: 0, page_size: 20 });
  const [submitting, setSubmitting] = useState(false);
  const [myRecommendation, setMyRecommendation] = useState(null);

  const statusOptions = [
    { value: "under_committee_review", label: "Under Committee Review" },
    { value: "committee_recommended", label: "Committee Recommended" },
    { value: "committee_hold", label: "Committee Hold" },
    { value: "committee_rejected", label: "Committee Rejected" },
  ];

  // Reviewable statuses where committee can submit recommendations
  const reviewableStatuses = ["under_committee_review"];

  useEffect(() => {
    fetchApplications();
  }, [filters, pagination.page]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        page_size: pagination.page_size,
      });
      
      if (filters.status && filters.status !== "all") params.append("status", filters.status);
      
      const response = await axios.get(`${API}/admin/applications?${params}`, {
        withCredentials: true
      });
      
      setApplications(response.data.applications || []);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicationDetail = async (applicationId) => {
    try {
      // Use the committee-specific endpoint that returns full details
      const response = await axios.get(`${API}/committee/review/${applicationId}`, {
        withCredentials: true
      });
      setAppDetails(response.data);
      setSelectedApp(response.data.application);
      
      // Check if current reviewer has already submitted a recommendation
      const reviewerId = response.data.reviewer_id;
      const assignment = response.data.committee_assignment;
      if (assignment && assignment.reviewers) {
        const myReview = assignment.reviewers.find(r => r.reviewer_id === reviewerId);
        if (myReview && myReview.has_submitted) {
          setMyRecommendation({
            decision: myReview.decision,
            comments: myReview.comments,
            submitted_at: myReview.submitted_at,
            reviewer_name: myReview.reviewer_name
          });
        } else {
          setMyRecommendation(null);
        }
      } else {
        setMyRecommendation(null);
      }
      
      setDetailModalOpen(true);
    } catch (error) {
      console.error("Error fetching application detail:", error);
      if (error.response?.status === 403) {
        toast.error("You are not assigned to review this application");
      } else {
        toast.error("Failed to load application details");
      }
    }
  };

  const handleDecision = async () => {
    if (!selectedApp || !actionType) return;
    
    // Validate comments for reject and hold
    if ((actionType === "reject" || actionType === "hold") && !comment.trim()) {
      toast.error("Comments are required for this recommendation");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/committee/review/${selectedApp.application_id}/decision`,
        {
          decision: actionType,
          comments: comment.trim(),
        },
        { withCredentials: true }
      );
      
      toast.success("Recommendation submitted successfully");
      
      // Set the submitted recommendation for read-only display
      setMyRecommendation({
        decision: actionType,
        comments: comment.trim(),
        submitted_at: new Date().toISOString(),
        reviewer_name: response.data?.reviewer_name || "You"
      });
      
      setActionModalOpen(false);
      setComment("");
      fetchApplications();
    } catch (error) {
      console.error("Error submitting decision:", error);
      if (error.response?.status === 409) {
        toast.error("You have already submitted a recommendation for this application");
        // Refresh to show the existing recommendation
        fetchApplicationDetail(selectedApp.application_id);
      } else {
        toast.error(error.response?.data?.detail || "Failed to submit recommendation");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openActionModal = (type) => {
    setActionType(type);
    setActionModalOpen(true);
  };

  const handleViewDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/secure/file/${doc.file_id}/view`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You do not have permission to view this document");
      } else {
        toast.error("Failed to load document");
      }
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      submitted: { label: "Submitted", color: "bg-blue-100 text-blue-800" },
      under_scrutiny: { label: "Under Scrutiny", color: "bg-purple-100 text-purple-800" },
      under_committee_review: { label: "Committee Review", color: "bg-violet-100 text-violet-800" },
      committee_recommended: { label: "Recommended", color: "bg-emerald-100 text-emerald-800" },
      committee_hold: { label: "On Hold", color: "bg-amber-100 text-amber-800" },
      committee_rejected: { label: "Rejected", color: "bg-red-100 text-red-800" },
      approved_pending_payment: { label: "Approved", color: "bg-green-100 text-green-800" },
      active: { label: "Active", color: "bg-teal-100 text-teal-800" },
      need_clarification: { label: "Clarification", color: "bg-amber-100 text-amber-800" },
    };
    const conf = config[status] || { label: status, color: "bg-slate-100 text-slate-800" };
    return <Badge className={`${conf.color} font-medium`}>{conf.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="committee-portal">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="font-heading font-bold text-2xl text-slate-900">
            Review Committee Portal
          </h1>
          <p className="text-slate-600 mt-1">
            Review and provide recommendations on membership applications assigned to you
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="border border-slate-200 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Shield className="w-4 h-4" />
                <span>Showing applications assigned to your committee</span>
              </div>
              <div className="flex-1" />
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger className="w-[220px]" data-testid="filter-status">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assigned</SelectItem>
                  {statusOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Applications Table */}
        <Card className="border border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No applications assigned to you</p>
                <p className="text-sm text-slate-500 mt-1">Applications will appear here when assigned by the Secretariat</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ref</TableHead>
                    <TableHead>Applicant</TableHead>
                    <TableHead>Membership Type</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((app) => (
                    <TableRow key={app.application_id} data-testid={`app-row-${app.application_id}`}>
                      <TableCell className="font-mono text-xs text-slate-600">
                        {app.application_id?.slice(-12) || "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{app.user_name || "N/A"}</p>
                            <p className="text-sm text-slate-500">{app.user_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">
                        {app.membership_type?.replace('_', ' ')}
                      </TableCell>
                      <TableCell>
                        {app.submission_date 
                          ? new Date(app.submission_date).toLocaleDateString()
                          : "N/A"
                        }
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(app.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchApplicationDetail(app.application_id)}
                          data-testid={`view-app-${app.application_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {!loading && pagination.total > pagination.page_size && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              Previous
            </Button>
            <span className="text-sm text-slate-600 px-4">
              Page {pagination.page} of {Math.ceil(pagination.total / pagination.page_size)}
            </span>
            <Button
              variant="outline"
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.page_size)}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Application Detail Modal - Full Read-Only View */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              Application Review
              <Badge className="ml-2 bg-blue-100 text-blue-800">Read-Only</Badge>
            </DialogTitle>
            <DialogDescription>
              Review all application details, documents, and provide your recommendation
            </DialogDescription>
          </DialogHeader>
          
          {appDetails && selectedApp && (
            <div className="space-y-6">
              {/* Applicant Header */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                  <User className="w-8 h-8 text-teal-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-lg text-slate-900">
                    {appDetails.user?.name || "Applicant"}
                  </h3>
                  <p className="text-slate-600">{appDetails.user?.email}</p>
                  <p className="text-sm text-slate-500">{appDetails.user?.phone}</p>
                </div>
                {getStatusBadge(selectedApp.status)}
              </div>

              {/* Tabs for Organized Sections */}
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid grid-cols-5 w-full">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="qualifications">Qualifications</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="secretariat">Secretariat Notes</TabsTrigger>
                  <TabsTrigger value="timeline">Timeline</TabsTrigger>
                </TabsList>

                {/* Application Details Tab */}
                <TabsContent value="details" className="space-y-4 mt-4">
                  {/* Basic Info */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Application Info
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Application ID</p>
                        <p className="font-mono text-xs">{selectedApp.application_id}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Membership Type</p>
                        <p className="capitalize font-medium">{selectedApp.membership_type?.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Submitted</p>
                        <p>{selectedApp.submission_date ? new Date(selectedApp.submission_date).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Personal Details */}
                  {selectedApp.personal_details && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <User className="w-4 h-4" /> Personal Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Date of Birth</p>
                          <p>{selectedApp.personal_details.date_of_birth || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Gender</p>
                          <p className="capitalize">{selectedApp.personal_details.gender || 'N/A'}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-slate-500">Address</p>
                          <p>{[
                            selectedApp.personal_details.house_number,
                            selectedApp.personal_details.street_address,
                            selectedApp.personal_details.city,
                            selectedApp.personal_details.state,
                            selectedApp.personal_details.pin_code,
                            selectedApp.personal_details.country
                          ].filter(Boolean).join(', ') || 'N/A'}</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Work Details */}
                  {selectedApp.work_details && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Briefcase className="w-4 h-4" /> Work Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Organization</p>
                          <p>{selectedApp.work_details.organization_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Designation</p>
                          <p>{selectedApp.work_details.designation || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Work Location</p>
                          <p>{[selectedApp.work_details.work_state, selectedApp.work_details.work_country].filter(Boolean).join(', ') || 'N/A'}</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Areas of Interest */}
                  {selectedApp.areas_of_interest && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Areas of Professional Interest</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-slate-700">{selectedApp.areas_of_interest}</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Qualifications Tab */}
                <TabsContent value="qualifications" className="space-y-4 mt-4">
                  {/* Academic Details */}
                  {selectedApp.academic_details && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <GraduationCap className="w-4 h-4" /> Academic Qualifications
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4 text-sm">
                        {selectedApp.academic_details.phd_course && (
                          <div className="p-3 bg-slate-50 rounded">
                            <p className="font-medium">PhD / Doctorate</p>
                            <p>{selectedApp.academic_details.phd_course}</p>
                            <p className="text-slate-500">{selectedApp.academic_details.phd_university} ({selectedApp.academic_details.phd_year})</p>
                          </div>
                        )}
                        {selectedApp.academic_details.pg_course && (
                          <div className="p-3 bg-slate-50 rounded">
                            <p className="font-medium">Post Graduate</p>
                            <p>{selectedApp.academic_details.pg_course}</p>
                            <p className="text-slate-500">{selectedApp.academic_details.pg_university} ({selectedApp.academic_details.pg_year})</p>
                          </div>
                        )}
                        {selectedApp.academic_details.graduation_course && (
                          <div className="p-3 bg-slate-50 rounded">
                            <p className="font-medium">Graduation</p>
                            <p>{selectedApp.academic_details.graduation_course}</p>
                            <p className="text-slate-500">{selectedApp.academic_details.graduation_university} ({selectedApp.academic_details.graduation_year})</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Professional Registration */}
                  {selectedApp.professional_details && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Award className="w-4 h-4" /> Professional Registration
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Years of Experience</p>
                          <p>{selectedApp.professional_details.years_of_experience || 'N/A'} years</p>
                        </div>
                        <div>
                          <p className="text-slate-500">RCI Status</p>
                          <p className="capitalize">{selectedApp.professional_details.rci_status?.replace('_', ' ') || 'N/A'}</p>
                        </div>
                        {selectedApp.professional_details.rci_number && (
                          <div>
                            <p className="text-slate-500">RCI Registration Number</p>
                            <p className="font-mono">{selectedApp.professional_details.rci_number}</p>
                          </div>
                        )}
                        {selectedApp.professional_details.rci_valid_until && (
                          <div>
                            <p className="text-slate-500">RCI Valid Until</p>
                            <p>{selectedApp.professional_details.rci_valid_until}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Documents Tab */}
                <TabsContent value="documents" className="mt-4">
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Uploaded Documents
                        <Badge className="ml-2 text-xs bg-slate-100 text-slate-600">View Only</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {appDetails.documents && appDetails.documents.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3">
                          {appDetails.documents.map((doc) => (
                            <div key={doc.file_id} className="border rounded-lg p-3 bg-white">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm capitalize">{doc.type?.replace(/_/g, ' ')}</p>
                                  <p className="text-xs text-slate-500 truncate">{doc.filename}</p>
                                  <p className="text-xs text-slate-400">
                                    {doc.uploaded_at || doc.created_at 
                                      ? new Date(doc.uploaded_at || doc.created_at).toLocaleDateString() 
                                      : ''}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewDocument(doc)}
                                  className="text-blue-700 hover:text-blue-800"
                                  data-testid={`view-doc-${doc.file_id}`}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Preview
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          <p>No documents uploaded</p>
                        </div>
                      )}
                      <p className="text-xs text-slate-400 mt-4 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        As a reviewer, you can preview documents but cannot download or modify them
                      </p>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Secretariat Notes Tab */}
                <TabsContent value="secretariat" className="mt-4">
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" /> Secretariat Checklist & Notes
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {appDetails.secretariat_reviews && appDetails.secretariat_reviews.length > 0 ? (
                        <div className="space-y-3">
                          {appDetails.secretariat_reviews.map((review, idx) => (
                            <div key={idx} className="p-3 bg-slate-50 rounded-lg border-l-4 border-purple-400">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-medium text-slate-900">{review.reviewer_name || 'Secretariat'}</span>
                                <span className="text-xs text-slate-500">
                                  {review.created_at && new Date(review.created_at).toLocaleString()}
                                </span>
                              </div>
                              {review.action && (
                                <Badge className="mb-2 text-xs">{review.action.replace(/_/g, ' ')}</Badge>
                              )}
                              {review.notes && <p className="text-sm text-slate-700">{review.notes}</p>}
                              {review.checklist_complete !== undefined && (
                                <p className="text-xs text-slate-500 mt-2">
                                  Checklist: {review.checklist_complete ? '✓ Complete' : '○ Incomplete'}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          <p>No secretariat notes available</p>
                        </div>
                      )}

                      {/* Correction Context if any */}
                      {selectedApp.correction_requests && selectedApp.correction_requests.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="text-sm font-medium mb-2 text-amber-700">Correction Requests</h4>
                          <div className="space-y-2">
                            {selectedApp.correction_requests.map((req, idx) => (
                              <div key={idx} className="p-2 bg-amber-50 rounded text-sm">
                                <p className="text-amber-800">{req.message}</p>
                                <p className="text-xs text-amber-600">{req.date && new Date(req.date).toLocaleDateString()}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Timeline Tab */}
                <TabsContent value="timeline" className="mt-4">
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <History className="w-4 h-4" /> Application Timeline
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {/* Committee Assignment Info */}
                        {appDetails.committee_assignment && (
                          <div className="p-3 bg-violet-50 rounded-lg border-l-4 border-violet-400">
                            <p className="text-sm font-medium text-violet-900">Assigned for Committee Review</p>
                            <p className="text-xs text-violet-700">
                              {appDetails.committee_assignment.assigned_at && new Date(appDetails.committee_assignment.assigned_at).toLocaleString()}
                            </p>
                            {appDetails.committee_assignment.expires_at && (
                              <p className="text-xs text-violet-600">
                                Review deadline: {new Date(appDetails.committee_assignment.expires_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Status History from Profile */}
                        {selectedApp.status_history && selectedApp.status_history.length > 0 ? (
                          <div className="space-y-2">
                            {selectedApp.status_history.slice().reverse().map((entry, idx) => (
                              <div key={idx} className="flex items-start gap-3 p-2 border-l-2 border-slate-200 pl-4">
                                <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium capitalize">{entry.status?.replace(/_/g, ' ')}</p>
                                  <p className="text-xs text-slate-500">
                                    {entry.timestamp && new Date(entry.timestamp).toLocaleString()}
                                    {entry.changed_by && ` by ${entry.changed_by}`}
                                  </p>
                                  {entry.notes && <p className="text-xs text-slate-600 mt-1">{entry.notes}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {selectedApp.submission_date && (
                              <div className="flex items-start gap-3 p-2 border-l-2 border-teal-300 pl-4">
                                <Calendar className="w-4 h-4 text-teal-500 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium">Application Submitted</p>
                                  <p className="text-xs text-slate-500">{new Date(selectedApp.submission_date).toLocaleString()}</p>
                                </div>
                              </div>
                            )}
                            {selectedApp.created_at && (
                              <div className="flex items-start gap-3 p-2 border-l-2 border-slate-200 pl-4">
                                <User className="w-4 h-4 text-slate-400 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium">Profile Created</p>
                                  <p className="text-xs text-slate-500">{new Date(selectedApp.created_at).toLocaleString()}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              <Separator />

              {/* Committee Comments */}
              {selectedApp.committee_comments && selectedApp.committee_comments.length > 0 && (
                <div>
                  <h4 className="font-heading font-semibold text-slate-900 mb-3">Previous Committee Comments</h4>
                  <div className="space-y-3">
                    {selectedApp.committee_comments.map((c, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <MessageSquare className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-900">{c.author_name}</span>
                          <span className="text-xs text-slate-500">
                            {new Date(c.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700">{c.comment}</p>
                        {c.status_change && (
                          <Badge className="mt-2 text-xs">{c.status_change}</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Committee Recommendation Panel */}
              <div className="pt-4 border-t" data-testid="recommendation-panel">
                <h4 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-teal-700" />
                  Committee Recommendation
                </h4>
                
                {/* Already Submitted - Read-Only Confirmation */}
                {myRecommendation ? (
                  <Card className="border-2 border-teal-200 bg-teal-50">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-teal-700 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium text-teal-900">Recommendation Submitted</p>
                          <div className="mt-2 space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-600">Decision:</span>
                              <Badge className={
                                myRecommendation.decision === "recommend" ? "bg-emerald-100 text-emerald-800" :
                                myRecommendation.decision === "reject" ? "bg-red-100 text-red-800" :
                                "bg-amber-100 text-amber-800"
                              }>
                                {myRecommendation.decision === "recommend" ? "Recommend Approve" :
                                 myRecommendation.decision === "reject" ? "Recommend Reject" :
                                 "Need Clarification"}
                              </Badge>
                            </div>
                            {myRecommendation.comments && (
                              <div><span className="text-slate-600">Comments:</span> {myRecommendation.comments}</div>
                            )}
                            <div className="text-xs text-slate-500">
                              By {myRecommendation.reviewer_name || "You"} on {myRecommendation.submitted_at ? new Date(myRecommendation.submitted_at).toLocaleString() : "just now"}
                            </div>
                          </div>
                          <p className="mt-3 text-xs text-slate-500">
                            The Secretariat will review your recommendation and make the final decision.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : reviewableStatuses.includes(selectedApp.status) ? (
                  /* Show Action Buttons - Only for reviewable status and not yet submitted */
                  <div>
                    <p className="text-sm text-slate-600 mb-3">
                      Submit your recommendation for this application. The Secretariat will make the final Accept/Reject decision.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={() => openActionModal("recommend")}
                        className="bg-teal-700 text-white hover:bg-teal-600"
                        data-testid="recommend-approve-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Recommend Approve
                      </Button>
                      <Button
                        onClick={() => openActionModal("reject")}
                        variant="destructive"
                        data-testid="recommend-reject-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Recommend Reject
                      </Button>
                      <Button
                        onClick={() => openActionModal("hold")}
                        variant="outline"
                        data-testid="need-clarification-btn"
                      >
                        <HelpCircle className="w-4 h-4 mr-2" />
                        Need Clarification
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Application not in reviewable status */
                  <Card className="border border-slate-200 bg-slate-50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 text-slate-600">
                        <AlertCircle className="w-5 h-5" />
                        <div>
                          <p className="font-medium">Recommendation Not Available</p>
                          <p className="text-sm">
                            {selectedApp.status === "committee_recommended" && "This application has already been recommended."}
                            {selectedApp.status === "committee_rejected" && "This application has already been rejected by committee."}
                            {selectedApp.status === "committee_hold" && "This application is on hold pending clarification."}
                            {!["committee_recommended", "committee_rejected", "committee_hold", "under_committee_review"].includes(selectedApp.status) && 
                              "This application is not currently under committee review."}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Modal */}
      <Dialog open={actionModalOpen} onOpenChange={setActionModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {actionType === "recommend" && "Recommend Approval"}
              {actionType === "reject" && "Recommend Rejection"}
              {actionType === "hold" && "Request Clarification"}
            </DialogTitle>
            <DialogDescription>
              {actionType === "recommend" 
                ? "Add optional comments explaining your approval recommendation."
                : "Comments are required for this recommendation. This will be visible to the Secretariat."
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2">
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={actionType === "recommend" ? "Optional comments..." : "Required: Explain your recommendation..."}
              rows={4}
              data-testid="action-comment"
              className={!comment.trim() && actionType !== "recommend" ? "border-amber-300" : ""}
            />
            {!comment.trim() && actionType !== "recommend" && (
              <p className="text-sm text-amber-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Comments are required for {actionType === "reject" ? "rejection" : "clarification"} recommendations
              </p>
            )}
            {actionType === "hold" && (
              <p className="text-xs text-slate-500">
                Note: Clarification requests are routed to the Secretariat, who will structure and send correction requests to the applicant.
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionModalOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleDecision} 
              data-testid="confirm-decision"
              disabled={submitting || (!comment.trim() && actionType !== "recommend")}
            >
              {submitting ? "Submitting..." : "Submit Recommendation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommitteePortal;
